import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import './App.css';
import { STOREFRONT_BRAND } from './utils/brandAssets';
import { usePageBrand } from './hooks/usePageBrand';

import TopBar from './components/TopBar';
import BodywonHeader from './components/BodywonHeader';
import BodywonHero from './components/BodywonHero';
import FeatureProducts from './components/FeatureProducts';
import NewArrival from './components/NewArrival';
import PantyCollection from './components/PantyCollection';
import PopularCollection from './components/PopularCollection';
import SiteFeatures from './components/SiteFeatures';
import AddBannerSlot from './components/AddBannerSlot';
import { AddBannerProvider } from './context/AddBannerContext';
import { SettingsProvider } from './context/SettingsContext';
import { CartProvider, useCart } from './context/CartContext';
import { LoginPromptProvider } from './context/LoginPromptContext';
import BodywonFooter from './components/BodywonFooter';
import CartOffcanvas from './components/CartOffcanvas';
import CartPage from './components/CartPage';
import CheckoutAddressPage from './address/CheckoutAddressPage';
import OrderReviewPage from './address/OrderReviewPage';
import OrderConfirmPage from './address/OrderConfirmPage';
import OrderHistoryPage from './order/OrderHistoryPage';
import OrderHistoryDetailPage from './order/OrderHistoryDetailPage';
import ProductDetailPage from './components/ProductDetailPage';
import AdminDashboard from './admin/pages/AdminDashboard';
import { AuthProvider } from './admin/auth/AuthContext';
import {
  bootstrapCatalogAccess,
  initializeAdminAuth,
  setAdminAuthToken,
} from './admin/jscontrol/apiClient';
import StorageManager from './admin/jscontrol/StorageManager';
import { canAccessAdminPanel, saveAdminReturnUrl } from './utils/authHelpers';
import CategoryPage from './admin/category/CategoryPage';
import ProductAttributePage from './admin/productattribute/ProductAttributePage';
import ProductAttributeValuePage from './admin/productattributevalue/ProductAttributeValuePage';
import UserPage from './admin/user/UserPage';
import UserCreate from './admin/user/UserCreate';
import UserEdit from './admin/user/UserEdit';
import ProductPage from './admin/product/ProductPage';
import ProductCreate from './admin/product/ProductCreate';
import ProductEdit from './admin/product/ProductEdit';
import BundleProductPage from './admin/bundleproduct/BundleProductPage';
import BundleProductCreate from './admin/bundleproduct/BundleProductCreate';
import BundleProductEdit from './admin/bundleproduct/BundleProductEdit';
import BannerPage from './admin/banner/BannerPage';
import BannerCreate from './admin/banner/BannerCreate';
import BannerEdit from './admin/banner/BannerEdit';
import AddBannerPage from './admin/addbanner/AddBannerPage';
import AddBannerCreate from './admin/addbanner/AddBannerCreate';
import AddBannerEdit from './admin/addbanner/AddBannerEdit';
import SettingPage from './admin/setting/SettingPage';
import AdminCartPage from './admin/cart/CartPage';
import AdminCartDetailPage from './admin/cart/CartDetailPage';
import OrderPage from './admin/order/OrderPage';
import OrderDetailPage from './admin/order/OrderDetailPage';
import ProductReviewPage from './admin/productreview/ProductReviewPage';

function StoreFrontContent({ showCart, setShowCart, showLogin, setShowLogin, catalogReady }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isCartPage = location.pathname === '/cart';
  const isCheckoutAddressPage = location.pathname === '/checkout/address';
  const isAccountAddressesPage = location.pathname === '/account/addresses';
  const isAccountOrdersPage = location.pathname === '/account/orders';
  const isAccountOrderDetailPage = location.pathname.startsWith('/account/orders/');
  const isOrderReviewPage = location.pathname === '/checkout/review';
  const isOrderConfirmPage = location.pathname === '/checkout/confirm';
  const isProductPage = location.pathname.startsWith('/product/');

  const {
    cartItems,
    itemCount,
    addToCart,
    addBundleToCart,
    removeFromCart,
    updateQuantity,
    refreshCart,
    processPendingAddToCart,
    clearCart,
  } = useCart();

  const handleLoginSuccess = async () => {
    await refreshCart();
    await processPendingAddToCart();
  };

  return (
    <>
      <TopBar />
      <BodywonHeader
        cartItemCount={itemCount}
        onCartClick={() => setShowCart(true)}
        onAddToCart={addToCart}
        showLogin={showLogin}
        setShowLogin={setShowLogin}
        onLoginSuccess={handleLoginSuccess}
        onLogoutSuccess={clearCart}
      />
      <main>
        {isCartPage ? (
          <CartPage onRequireLogin={() => setShowLogin(true)} />
        ) : isCheckoutAddressPage ? (
          <CheckoutAddressPage onRequireLogin={() => setShowLogin(true)} />
        ) : isAccountAddressesPage ? (
          <CheckoutAddressPage mode="account" onRequireLogin={() => setShowLogin(true)} />
        ) : isAccountOrdersPage ? (
          <OrderHistoryPage onRequireLogin={() => setShowLogin(true)} />
        ) : isAccountOrderDetailPage ? (
          <OrderHistoryDetailPage onRequireLogin={() => setShowLogin(true)} />
        ) : isOrderReviewPage ? (
          <OrderReviewPage onRequireLogin={() => setShowLogin(true)} />
        ) : isOrderConfirmPage ? (
          <OrderConfirmPage onRequireLogin={() => setShowLogin(true)} />
        ) : isProductPage ? (
          <ProductDetailPage onAddToCart={addToCart} onAddBundleToCart={addBundleToCart} />
        ) : (
          <>
            <BodywonHero catalogReady={catalogReady} />
            <AddBannerSlot displayOrder={1} />
            <FeatureProducts onAddToCart={addToCart} catalogReady={catalogReady} />
            <AddBannerSlot displayOrder={2} />
            <NewArrival onAddToCart={addToCart} />
            <AddBannerSlot displayOrder={3} />
            <PantyCollection onAddToCart={addToCart} catalogReady={catalogReady} />
            <AddBannerSlot displayOrder={4} />
            <PopularCollection onAddToCart={addToCart} catalogReady={catalogReady} />
            <AddBannerSlot displayOrder={5} />
            <SiteFeatures />
          </>
        )}
      </main>
      <BodywonFooter />
      <CartOffcanvas
        show={showCart}
        onHide={() => setShowCart(false)}
        cartItems={cartItems}
        onRemove={removeFromCart}
        onUpdateQuantity={updateQuantity}
        onViewCart={() => {
          setShowCart(false);
          navigate('/cart');
        }}
      />
    </>
  );
}

function StoreFront() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showCart, setShowCart] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [catalogReady, setCatalogReady] = useState(false);

  usePageBrand({ title: STOREFRONT_BRAND.title, favicon: STOREFRONT_BRAND.favicon });

  useEffect(() => {
    bootstrapCatalogAccess().finally(() => setCatalogReady(true));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('login') !== '1') return;

    setShowLogin(true);
    const returnUrl = params.get('returnUrl');
    if (returnUrl) saveAdminReturnUrl(returnUrl);
    navigate('/', { replace: true });
  }, [location.search, navigate]);

  return (
    <SettingsProvider catalogReady={catalogReady}>
      <AddBannerProvider catalogReady={catalogReady}>
        <CartProvider
          onRequireLogin={() => setShowLogin(true)}
          onCartOpen={() => setShowCart(true)}
        >
          <LoginPromptProvider onRequireLogin={() => setShowLogin(true)}>
          <StoreFrontContent
            showCart={showCart}
            setShowCart={setShowCart}
            showLogin={showLogin}
            setShowLogin={setShowLogin}
            catalogReady={catalogReady}
          />
          </LoginPromptProvider>
        </CartProvider>
      </AddBannerProvider>
    </SettingsProvider>
  );
}

function AdminApp() {
  const location = useLocation();
  const [isLoggedIn, setIsLoggedIn] = useState(null);

  useEffect(() => {
    initializeAdminAuth().then((token) => {
      if (!token) {
        setIsLoggedIn(false);
        return;
      }
      const role = StorageManager.roleName();
      const username = StorageManager.username();
      if (!canAccessAdminPanel({ roleName: role, username })) {
        setAdminAuthToken(null);
        setIsLoggedIn(false);
        return;
      }
      setIsLoggedIn(true);
    });
  }, []);

  if (isLoggedIn === null) return null;

  if (!isLoggedIn) {
    const returnUrl = `${location.pathname}${location.search}${location.hash}`;
    const params = new URLSearchParams({ login: '1', returnUrl });
    return <Navigate to={`/?${params.toString()}`} replace />;
  }

  return (
    <AuthProvider>
        <SettingsProvider catalogReady={true}>
        <Routes>
          <Route index element={<AdminDashboard onLogout={() => {
            setAdminAuthToken(null);
            setIsLoggedIn(false);
          }} />} />
          <Route path="category" element={<CategoryPage />} />
          <Route path="productattribute" element={<ProductAttributePage />} />
          <Route path="productattributevalue" element={<ProductAttributeValuePage />} />
          <Route path="user" element={<UserPage />} />
          <Route path="user/create" element={<UserCreate />} />
          <Route path="user/edit/:id" element={<UserEdit />} />
          <Route path="product" element={<ProductPage />} />
          <Route path="product/create" element={<ProductCreate />} />
          <Route path="product/edit/:id" element={<ProductEdit />} />
          <Route path="bundleproduct" element={<BundleProductPage />} />
          <Route path="bundleproduct/create" element={<BundleProductCreate />} />
          <Route path="bundleproduct/edit/:id" element={<BundleProductEdit />} />
          <Route path="banner" element={<BannerPage />} />
          <Route path="banner/create" element={<BannerCreate />} />
          <Route path="banner/edit/:id" element={<BannerEdit />} />
          <Route path="addbanner" element={<AddBannerPage />} />
          <Route path="addbanner/create" element={<AddBannerCreate />} />
          <Route path="addbanner/edit/:id" element={<AddBannerEdit />} />
          <Route path="setting" element={<SettingPage />} />
          <Route path="cart" element={<AdminCartPage />} />
          <Route path="cart/view/:id" element={<AdminCartDetailPage />} />
          <Route path="order" element={<OrderPage />} />
          <Route path="order/view/:id" element={<OrderDetailPage />} />
          <Route path="productreview" element={<ProductReviewPage />} />
        </Routes>
        </SettingsProvider>
    </AuthProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <div className="App">
        <ToastContainer position="top-right" autoClose={3000} />
        <Routes>
          <Route path="/product/:id" element={<StoreFront />} />
          <Route path="/cart" element={<StoreFront />} />
          <Route path="/checkout/address" element={<StoreFront />} />
          <Route path="/account/addresses" element={<StoreFront />} />
          <Route path="/account/orders" element={<StoreFront />} />
          <Route path="/account/orders/:id" element={<StoreFront />} />
          <Route path="/checkout/review" element={<StoreFront />} />
          <Route path="/checkout/confirm" element={<StoreFront />} />
          <Route path="/" element={<StoreFront />} />
          <Route path="/admin/*" element={<AdminApp />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;
